import React, { useMemo, useState } from 'react';
import { Input, Modal, Select, Switch, Table, Tooltip, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { DeleteIcon } from '@/assets/icons/icons';

import { isPossiblePhoneNumber } from 'libphonenumber-js';

import { CustomButton, CustomTitle } from '../../../components';
import { useToken, getInitials } from '../../../common/helper';
import { AddUsers } from '../../../apis/onboarding';
import type { BulkInviteTeam, BulkInviteUserRow } from '../../../apis/settings';
import useUnauthorize from '../../../hooks/useUnauthorize';
import theme from '@/assets/theme';

import './InviteUserModal.scss';

interface RoleOption {
  id: string;
  name: string;
}

interface ImportUsersReviewModalProps {
  open: boolean;
  users: BulkInviteUserRow[];
  onUsersChange: (users: BulkInviteUserRow[]) => void;
  onClose: () => void;
  onSuccess: (invitedCount: number) => void;
  /** Refresh the users listing without closing the modal (partial success). */
  onRefresh: () => void;
  allTeams: Array<{ id: string; name: string; color?: string }>;
  roleList: RoleOption[];
}

const validateRow = (row: BulkInviteUserRow): Record<string, string> => {
  const errors: Record<string, string> = {};

  // Name
  // Name — keep in sync with InviteUserModal + backend bulk-parse (min 3).
  if (!row.name?.trim()) errors.name = 'Name is required';
  else if (row.name.trim().length < 3)
    errors.name = 'Name must be at least 3 characters';

  // Email
  if (!row.email?.trim()) errors.email = 'Email is required';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email.trim()))
    errors.email = 'Enter a valid email';

  // Phone (optional — only validate if provided)
  if (row.phone_number?.trim()) {
    try {
      if (!isPossiblePhoneNumber(row.phone_number, 'US'))
        errors.phone_number = 'Invalid phone number';
    } catch {
      errors.phone_number = 'Invalid phone number';
    }
  }

  // Teams — at least one assignment, every entry must have both team + role
  if (!row.teams?.length) {
    errors.teams = 'At least one team + role is required';
  } else if (row.teams.some((t) => !t.team || !t.role)) {
    errors.teams = 'Each assignment needs both a team and a role';
  }

  return errors;
};

/** Stable identity for a row; `key` is set at parse time but may be absent. */
const rowId = (row: Pick<BulkInviteUserRow, 'key' | 'row'>): string =>
  row.key || String(row.row);

const emailKeyOf = (row: Pick<BulkInviteUserRow, 'email'>): string =>
  (row.email || '').trim().toLowerCase();

const AVATAR_COLORS = [
  '#266EF1',
  '#0E8345',
  '#7944DB',
  '#E11D48',
  '#CA8A04',
  '#0F766E',
  '#C2410C',
];

const ImportUsersReviewModal: React.FC<ImportUsersReviewModalProps> = ({
  open,
  users,
  onUsersChange,
  onClose,
  onSuccess,
  onRefresh,
  allTeams,
  roleList,
}) => {
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const [submitting, setSubmitting] = useState(false);

  const rowsWithValidation = useMemo(() => {
    const seenEmails = new Map<string, number>();
    return users.map((u) => {
      // Start with backend errors (e.g. "Email already exists"), then layer frontend checks on top
      const errors: Record<string, string> = { ...(u.errors || {}) };

      // Frontend format checks — only override if backend didn't already flag the field
      const feErrors = validateRow(u);
      for (const [field, msg] of Object.entries(feErrors)) {
        if (!errors[field]) errors[field] = msg;
      }

      // File-level duplicate detection
      const emailKey = emailKeyOf(u);
      if (emailKey && !errors.email) {
        if (seenEmails.has(emailKey)) {
          errors.email = `Duplicate email (row ${seenEmails.get(emailKey)})`;
        } else {
          seenEmails.set(emailKey, u.row ?? 0);
        }
      }
      return { ...u, errors };
    });
  }, [users]);

  const validRows = rowsWithValidation.filter(
    (u) => Object.keys(u.errors).length === 0
  );

  const updateRow = (
    key: string | undefined,
    patch: Partial<BulkInviteUserRow>
  ) => {
    onUsersChange(
      users.map((u) => {
        if (rowId(u) !== key) return u;
        // Clear stale backend errors for every field being patched so frontend
        // validation re-evaluates them from the new value
        const clearedErrors = { ...(u.errors || {}) };
        const fieldToErrorKey: Record<string, string> = {
          name: 'name',
          email: 'email',
          phone_number: 'phone_number',
          send_email: 'send_email',
          teams: 'teams',
          title: 'title',
        };
        for (const patchedField of Object.keys(patch)) {
          const errKey = fieldToErrorKey[patchedField] ?? patchedField;
          delete clearedErrors[errKey];
        }
        return { ...u, ...patch, errors: clearedErrors };
      })
    );
  };

  const removeRow = (key: string | undefined) => {
    onUsersChange(users.filter((u) => rowId(u) !== key));
  };

  const handleInvite = () => {
    if (validRows.length === 0) {
      message.error('No valid users to invite');
      return;
    }

    const payload = validRows.map((u) => ({
      name: u.name.trim(),
      title: u.title?.trim() || '',
      email: u.email.trim(),
      phone_number: u.phone_number || '',
      send_email: u.send_email !== false,
      id: null as number | null,
      teams: (u.teams || []).map((t) => ({
        team: t.team,
        role: t.role,
      })),
    }));

    setSubmitting(true);
    AddUsers(token ?? '', payload, false)
      .then((res) => {
        const data = res?.data;
        if (data?.data?.is_success) {
          const result = (data?.data?.data || {}) as {
            created_users?: unknown;
            errors?: Array<{ email?: string; errors?: string }>;
          };
          const created = result.created_users;
          // Bulk payload is always a list, so created_users is an array or null.
          const createdCount = Array.isArray(created)
            ? created.length
            : created
              ? 1
              : 0;
          const apiErrors = result.errors;
          if (Array.isArray(apiErrors) && apiErrors.length > 0) {
            const failedEmails = new Set(
              apiErrors
                .map((e) => (e.email || '').trim().toLowerCase())
                .filter(Boolean)
            );
            const submittedIds = new Set(validRows.map(rowId));
            // Keep the rows that failed (re-labelled with their API error) plus the
            // rows we never submitted, so the user can fix everything and retry.
            // Rows that were invited successfully drop out of the table.
            onUsersChange(
              users
                .filter(
                  (u) =>
                    failedEmails.has(emailKeyOf(u)) ||
                    !submittedIds.has(rowId(u))
                )
                .map((u) =>
                  failedEmails.has(emailKeyOf(u))
                    ? {
                        ...u,
                        errors: {
                          ...(u.errors || {}),
                          email:
                            apiErrors.find(
                              (e) =>
                                (e.email || '').trim().toLowerCase() ===
                                emailKeyOf(u)
                            )?.errors || 'Invite failed',
                        },
                      }
                    : u
                )
            );
            if (createdCount > 0) {
              onRefresh();
              message.warning(
                `${createdCount} invited, ${apiErrors.length} failed. Fix the highlighted rows and retry.`
              );
            } else {
              message.error(
                'Invites failed. Fix the highlighted rows and retry.'
              );
            }
          } else {
            onSuccess(createdCount || validRows.length);
          }
        } else {
          message.error(data?.data?.message || 'Invite failed');
        }
      })
      .catch((error) => unauthorize(error))
      .finally(() => setSubmitting(false));
  };

  const teamOptions = (allTeams || []).map((t) => ({
    label: String(t.name || ''),
    value: String(t.id || ''),
    color: String(t.color || ''),
  }));

  const roleOptions = roleList.map((r) => ({
    label: r.name,
    value: r.id,
  }));

  const columns = [
    {
      title: 'Name / title',
      key: 'name',
      width: 240,
      render: (
        _: unknown,
        record: BulkInviteUserRow & { errors: Record<string, string> }
      ) => {
        const rowKey = rowId(record);
        const color =
          AVATAR_COLORS[
            Math.abs(
              (record.email || record.name || '')
                .split('')
                .reduce((a, c) => a + c.charCodeAt(0), 0)
            ) % AVATAR_COLORS.length
          ];
        return (
          <div className='import-users-modal__name-cell'>
            {Object.keys(record.errors).length > 0 && (
              <Tooltip
                title={Object.values(record.errors).join(' · ')}
                color='var(--danger)'
              >
                <span className='import-users-modal__row-error-icon'>!</span>
              </Tooltip>
            )}
            <div
              className='import-users-modal__avatar'
              // background comes from the per-user palette colour
              // eslint-disable-next-line react/forbid-dom-props
              style={{ backgroundColor: color }}
            >
              {getInitials(record.name || '') || '--'}
            </div>
            <div className='import-users-modal__name-stack'>
              <Tooltip title={record.errors.name}>
                <Input
                  value={record.name}
                  onChange={(e) => updateRow(rowKey, { name: e.target.value })}
                  className={`import-users-modal__input import-users-modal__name-input ${
                    record.errors.name ? 'import-users-modal__cell-error' : ''
                  }`}
                  placeholder='Name'
                />
              </Tooltip>
              <Input
                value={record.title}
                onChange={(e) => updateRow(rowKey, { title: e.target.value })}
                className='import-users-modal__input import-users-modal__title-input'
                placeholder='Title'
              />
            </div>
          </div>
        );
      },
    },
    {
      title: 'Email',
      key: 'email',
      width: 200,
      render: (
        _: unknown,
        record: BulkInviteUserRow & { errors: Record<string, string> }
      ) => {
        const rowKey = rowId(record);
        return (
          <Tooltip title={record.errors.email}>
            <Input
              value={record.email}
              onChange={(e) => updateRow(rowKey, { email: e.target.value })}
              className={`import-users-modal__input ${
                record.errors.email ? 'import-users-modal__cell-error' : ''
              }`}
              placeholder='Email'
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'Phone',
      key: 'phone',
      width: 180,
      render: (
        _: unknown,
        record: BulkInviteUserRow & { errors: Record<string, string> }
      ) => {
        const rowKey = rowId(record);
        return (
          <Tooltip title={record.errors.phone_number}>
            <Input
              value={record.phone_number || ''}
              onChange={(e) =>
                updateRow(rowKey, { phone_number: e.target.value })
              }
              className={`import-users-modal__input ${
                record.errors.phone_number
                  ? 'import-users-modal__cell-error'
                  : ''
              }`}
              placeholder='Phone (optional)'
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'Send email',
      key: 'send_email',
      width: 110,
      render: (_: unknown, record: BulkInviteUserRow) => {
        const rowKey = rowId(record);
        return (
          <Switch
            size='small'
            checked={record.send_email !== false}
            onChange={(checked) => updateRow(rowKey, { send_email: checked })}
            aria-label='Send set-password email'
          />
        );
      },
    },
    {
      title: theme.themeType === 2 ? 'Client: Role' : 'Team: Role',
      key: 'teams',
      width: 280,
      ellipsis: true,
      render: (
        _: unknown,
        record: BulkInviteUserRow & { errors: Record<string, string> }
      ) => {
        const rowKey = rowId(record);
        const teams = record.teams || [];

        return (
          <Tooltip title={record.errors.teams}>
            <div
              className={`import-users-modal__teams ${
                record.errors.teams ? 'import-users-modal__cell-error' : ''
              }`}
            >
              {/* Each existing assignment: colour dot + team name + role dropdown + × */}
              {teams.map((t, idx) => (
                <div key={t.team} className='import-users-modal__team-row'>
                  <span
                    className='import-users-modal__team-dot'
                    // background comes from the team's own colour
                    // eslint-disable-next-line react/forbid-dom-props
                    style={{ background: t.color || 'var(--gray-600)' }}
                  />
                  <Tooltip title={t.team_name} placement='topLeft'>
                    <span className='import-users-modal__team-name'>
                      {t.team_name}
                    </span>
                  </Tooltip>
                  <span className='import-users-modal__team-colon'>:</span>
                  <Select
                    size='small'
                    value={t.role || undefined}
                    className='import-users-modal__team-role-select'
                    options={roleOptions}
                    onChange={(roleId: string) => {
                      const role = roleOptions.find((r) => r.value === roleId);
                      if (!role) return;
                      const updated = teams.map((x, i) =>
                        i === idx
                          ? { ...x, role: role.value, role_name: role.label }
                          : x
                      );
                      updateRow(rowKey, { teams: updated });
                    }}
                  />
                  <button
                    type='button'
                    className='import-users-modal__team-remove'
                    onClick={() =>
                      updateRow(rowKey, {
                        teams: teams.filter((_, i) => i !== idx),
                      })
                    }
                  >
                    ×
                  </button>
                </div>
              ))}

              {/* Add team dropdown — resets to placeholder after each pick */}
              {teamOptions.filter((o) => !teams.find((t) => t.team === o.value))
                .length > 0 && (
                <Select
                  size='small'
                  placeholder='Add team'
                  style={{ minWidth: 110 }}
                  value={null}
                  options={teamOptions.filter(
                    (o) => !teams.find((t) => t.team === o.value)
                  )}
                  onChange={(value: string) => {
                    const team = teamOptions.find((t) => t.value === value);
                    const defaultRole = roleOptions[0];
                    if (!team) return;
                    const next: BulkInviteTeam = {
                      team: team.value,
                      role: defaultRole?.value ?? '',
                      team_name: team.label,
                      role_name: defaultRole?.label ?? '',
                      color: team.color,
                    };
                    updateRow(rowKey, { teams: [...teams, next] });
                  }}
                />
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 48,
      fixed: 'right' as const,
      render: (_: unknown, record: BulkInviteUserRow) => {
        const rowKey = rowId(record);
        return (
          <button
            type='button'
            className='invite-user-modal__icon-btn'
            onClick={() => removeRow(rowKey)}
            aria-label='Remove row'
          >
            <DeleteIcon color='currentColor' />
          </button>
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1100}
      centered
      destroyOnHidden
      maskClosable={false}
      className='import-users-modal'
      closable={false}
      styles={{
        body: { padding: 0 },
      }}
    >
      <div className='import-users-modal__header'>
        <CustomTitle
          text='Import users'
          fontSize={20}
          fontWeight='700'
          lineHeight='28px'
          color='var(--text-primary)'
          marginBottom={0}
        />
        <button
          type='button'
          className='invite-user-modal__icon-btn'
          onClick={onClose}
          aria-label='Close'
        >
          <CloseOutlined />
        </button>
      </div>

      <div className='import-users-modal__body'>
        <Table
          rowKey={(r) => rowId(r)}
          columns={columns}
          dataSource={rowsWithValidation}
          pagination={false}
          scroll={{ y: 420, x: 1000 }}
          className='settings-table'
          rowHoverable={false}
          size='middle'
          rowClassName={(r) =>
            Object.keys(r.errors).length > 0
              ? 'import-users-modal__row--error'
              : ''
          }
        />
      </div>

      <div className='import-users-modal__footer'>
        <span className='import-users-modal__count'>
          {validRows.length} user{validRows.length === 1 ? '' : 's'} will be
          invited
        </span>
        <div className='import-users-modal__footer-actions'>
          <button
            type='button'
            className='import-users-modal__cancel'
            onClick={onClose}
          >
            Cancel
          </button>
          <CustomButton
            text='Invite users'
            width='140px'
            height='40px'
            borderRadius='8px'
            bgColor={submitting || validRows.length === 0 ? '#F3F3F3' : '#000'}
            color={submitting || validRows.length === 0 ? '#A6A6A6' : '#fff'}
            border='0px'
            fontSize='14px'
            fontWeight={600}
            onClick={handleInvite}
            isFormValid={submitting || validRows.length === 0}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ImportUsersReviewModal;
